
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHelmetProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  jsonLd?: Record<string, unknown>;
}

const DEFAULT_OG_IMAGE = 'https://propico.vn/lovable-uploads/202e7a7e-e459-4302-b6e8-be33ce8b7430.png';
const DEFAULT_OG_IMAGE_ALT = 'Propico — Dịch vụ quản lý cho thuê căn hộ chuyên nghiệp tại Việt Nam';

const SEOHelmet: React.FC<SEOHelmetProps> = ({
  title,
  description,
  canonicalUrl,
  ogImage,
  ogImageAlt,
  ogTitle,
  ogDescription,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  jsonLd,
}) => {
  const location = useLocation();
  const baseUrl = "https://propico.vn";
  
  useEffect(() => {
    const currentCanonical = canonicalUrl || `${baseUrl}${location.pathname}`;
    const finalOgImage = ogImage || DEFAULT_OG_IMAGE;
    const finalOgImageAlt = ogImageAlt || DEFAULT_OG_IMAGE_ALT;
    const finalOgTitle = ogTitle || title;
    const finalOgDescription = ogDescription || description;

    // Canonical
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', currentCanonical);
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', currentCanonical);
      document.head.appendChild(canonicalLink);
    }

    // Title
    if (title) {
      document.title = title;
    }

    // Meta description
    if (description) {
      updateMeta('name', 'description', description);
    }

    // OG tags (standardized)
    updateMeta('property', 'og:type', ogType);
    updateMeta('property', 'og:site_name', 'Propico');
    updateMeta('property', 'og:locale', 'vi_VN');
    updateMeta('property', 'og:url', currentCanonical);
    if (finalOgTitle) updateMeta('property', 'og:title', finalOgTitle);
    if (finalOgDescription) updateMeta('property', 'og:description', finalOgDescription);
    updateMeta('property', 'og:image', finalOgImage);
    updateMeta('property', 'og:image:alt', finalOgImageAlt);
    updateMeta('property', 'og:image:width', '1200');
    updateMeta('property', 'og:image:height', '630');

    // Twitter Card (standardized)
    updateMeta('name', 'twitter:card', twitterCard);
    updateMeta('name', 'twitter:url', currentCanonical);
    if (finalOgTitle) updateMeta('name', 'twitter:title', finalOgTitle);
    if (finalOgDescription) updateMeta('name', 'twitter:description', finalOgDescription);
    updateMeta('name', 'twitter:image', finalOgImage);
    updateMeta('name', 'twitter:image:alt', finalOgImageAlt);


    // JSON-LD per page
    const existingScript = document.querySelector('script[data-seo-page]');
    if (jsonLd) {
      if (existingScript) {
        existingScript.textContent = JSON.stringify(jsonLd);
      } else {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-page', 'true');
        script.textContent = JSON.stringify(jsonLd);
        document.head.appendChild(script);
      }
    } else if (existingScript) {
      existingScript.remove();
    }

    return () => {
      // Cleanup page-specific JSON-LD on unmount
      const script = document.querySelector('script[data-seo-page]');
      if (script) script.remove();
    };
  }, [location.pathname, title, description, canonicalUrl, ogImage, ogImageAlt, ogTitle, ogDescription, ogType, twitterCard, jsonLd, baseUrl]);

  return null;
};

function updateMeta(attr: 'name' | 'property', key: string, value: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (el) {
    el.setAttribute('content', value);
  } else {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute('content', value);
    document.head.appendChild(el);
  }
}

export default SEOHelmet;
